import { FormEvent, ReactElement, useRef, useState } from "react";
import { Link } from "react-router-dom";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import { SecondmentActor, SecondmentDetail, submitSecondment } from "../../../Services/PublicSecondments";
import SecondmentsTabs from "./SecondmentsTabs";

interface SecondmentsApplyProps {
  detail: SecondmentDetail;
  actor: SecondmentActor;
  mode: "app" | "info";
  applicantPlace: string;
}

function draft(mode: "app" | "info", firmName: string, place: string, hasPosting: boolean): string {
  const where = place || "its jurisdiction";
  const opening = `I am an attorney at ${firmName} firm, the WSG member firm for ${where}, I recently saw your listing for a secondment and `;
  if (mode === "app") {
    return `${opening}I am interested in the opportunity. <ADDITIONAL DESCRIPTION>\n\nPlease let me know the next steps.\n\nBest regards,`;
  }
  if (hasPosting) {
    return `${opening}I would like some more information regarding this position. <QUESTIONS>\n\nI look forward to your response.\n\nBest regards,`;
  }
  return `${opening}I would like some more information regarding this position.\n\nI look forward to your response.\n\nBest regards,`;
}

export default function SecondmentsApply({ detail, actor, mode, applicantPlace }: SecondmentsApplyProps): ReactElement {
  const [method, setMethod] = useState("1");
  const [comment, setComment] = useState(draft(mode, actor.firmName, applicantPlace, detail.secId > 0));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const title = mode === "app" ? "Apply" : "Contact Firm";

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    try {
      const result = await submitSecondment(mode === "app" ? "apply" : "info", actor, {
        secId: String(detail.secId),
        comment,
        method,
        dateFrom,
        dateTo,
      });
      if (!result.saved) {
        setErrors(result.errors || ["The request could not be saved."]);
        return;
      }
      setSaved(true);
    } catch {
      setErrors(["The request could not be saved."]);
    }
  }

  if (detail.alreadyApplied && mode === "app") {
    return (
      <section id="view">
        <h2 className="secondments-heading">Apply</h2>
        <SecondmentsTabs secId={detail.secId} firmId={detail.firmId} active="app" posting />
        <p className="secondments-error">Your Secondment Application has been submitted.</p>
      </section>
    );
  }

  if (saved) {
    return (
      <section id="view">
        <h2 className="secondments-heading">Contact Firm</h2>
        <p className="secondments-error">Your request has been sent to the firm. You will soon receive a copy of your request.</p>
        <Link to="/dealmaker.asp"><NewButton text="Ok" color="primary" style="filled" /></Link>
      </section>
    );
  }

  return (
    <section id="view">
      <h2 className="secondments-heading">{title}</h2>
      <SecondmentsTabs secId={detail.secId} firmId={detail.firmId} active={mode} posting={detail.secId > 0} />
      {errors.length > 0 ? (
        <div className="secondments-error">
          <p>Please correct the following error(s)</p>
          {errors.map((error) => <p key={error}>{error}</p>)}
        </div>
      ) : null}
      <form ref={formRef} className="secondments-form" onSubmit={onSubmit}>
        <p>
          {detail.secId > 0 ? <><strong>Re</strong>: {detail.positionTitle}<br /></> : null}
          <strong>To:</strong><br />
          <strong>Firm: </strong>{detail.companyName}<br />
          <strong>Contact Name: </strong><a href={`/profile/${detail.contactSlug}/${detail.contactId}`}>{detail.contactName}</a><br />
          <strong>Email: </strong><a href={`mailto:${detail.contactEmail}`}>{detail.contactEmail}</a><br />
          <strong>Phone: </strong>{detail.contactPhone}
        </p>
        <p>
          <span className="secondments-label">From:</span><br />
          {actor.name}<br />{actor.title}<br />{actor.firmName}<br />{actor.email}<br />{actor.phone}
        </p>
        <p>Preferred Method of Communications</p>
        <div className="secondments-checks">
          <label><input type="radio" name="method" value="1" checked={method === "1"} onChange={() => setMethod("1")} /> Email</label>
          <label><input type="radio" name="method" value="0" checked={method === "0"} onChange={() => setMethod("0")} /> Phone</label>
        </div>
        {mode === "app" ? (
          <p>
            <span className="secondments-label">Duration of Interest*</span>
            <input type="date" aria-label="From" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            <input type="date" aria-label="To" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </p>
        ) : null}
        <label className="secondments-label">
          {mode === "app" ? "Secondment Description of Interest" : "Comments and Questions*"}
          <textarea rows={6} value={comment} onChange={(event) => setComment(event.target.value)} />
        </label>
        <NewButton text="Submit" color="primary" style="filled" onClick={() => formRef.current?.requestSubmit()} />
      </form>
    </section>
  );
}
